import styled from 'styled-components'

/**
 * @author Tecnel MC
 * @name Button
 * @description API for button
 * @example
 * <Button type='primary' outline>Button</Button>
*/

// Semantic colors - now using theme
const getSemanticColor = (theme, type) => {
  const colors = {
    primary: theme.colors.semantic.primary,
    success: theme.colors.semantic.success,
    danger: theme.colors.semantic.danger,
    warning: theme.colors.semantic.warning,
    info: theme.colors.semantic.info,
    secondary: theme.colors.semantic.secondary
  }
  return colors[type] || theme.colors.text.secondary
}

export const Button = styled.button`
  min-width: ${({ width }) => width || 'auto'};
  height: ${props => props.theme.controlHeight};
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 0.375rem;
  border-radius: ${props => props.theme.radius.button};
  padding: 0 1rem;
  background: ${({ type, outline, disabled, theme }) => {
    if (disabled) return theme.colors.background.disabled
    if (outline) return theme.colors.background.primary
    if (!type) return theme.colors.background.primary
    if (type === 'primary') return theme.colors.semantic.primary
    return theme.colors.background.primary
  }};
  border: 1px solid ${({ type, outline, disabled, theme }) => {
    if (disabled) return theme.colors.border.medium
    if (outline || !type) return theme.colors.border.medium
    if (type === 'primary') return theme.colors.semantic.primary
    return theme.colors.border.medium
  }};
  color: ${({ type, outline, disabled, theme }) => {
    if (disabled) return theme.colors.text.disabled
    if (outline || !type) {
      return getSemanticColor(theme, type)
    }
    if (type === 'primary') return theme.colors.text.inverse
    return getSemanticColor(theme, type)
  }};
  font-size: ${props => props.theme.typography.fontSize.button};
  font-weight: ${props => props.theme.typography.fontWeight.button};
  font-family: ${props => props.theme.typography.fontFamily};
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: ${props => props.theme.transitions.button};
  white-space: nowrap;
  position: relative;
  user-select: none;
  box-shadow: ${({ type, outline, disabled, theme }) => {
    if (disabled) return 'none'
    if (type === 'primary' && !outline) return `0 1px 2px ${theme.colors.shadow.light}`
    return 'none'
  }};
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: ${({ type, outline, theme }) => {
      if (outline || !type) return theme.colors.background.hover
      if (type === 'primary') return theme.colors.semantic.primary
      if (type === 'success') return theme.colors.semantic.successHover
      return theme.colors.background.hover
    }};
    border-color: ${({ type, outline, theme }) => {
      if (type === 'primary' && !outline) return theme.colors.semantic.primary
      return theme.colors.border.strong
    }};
    box-shadow: 0 4px 6px -1px ${props => props.theme.colors.shadow.medium};
    opacity: ${({ type }) => type === 'primary' ? 0.9 : 1};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: none;
    background: ${({ type, outline, theme }) => {
      if (outline || !type) return theme.colors.background.tertiary
      if (type === 'primary') return theme.colors.semantic.primary
      return theme.colors.background.tertiary
    }};
    opacity: ${({ type }) => type === 'primary' ? 0.8 : 1};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.colors.shadow.focus};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    background: ${props => props.theme.colors.background.disabled};
    border-color: ${props => props.theme.colors.border.medium};
    color: ${props => props.theme.colors.text.disabled};

    &:hover {
      transform: none;
      box-shadow: none;
      background: ${props => props.theme.colors.background.disabled};
      filter: none;
    }
  }

  svg {
    font-size: 0.75rem;
    flex-shrink: 0;
    opacity: 0.8;
  }
`

export const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: auto;
`

export const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid ${props => props.theme.colors.border.medium};
  background: ${props => props.theme.colors.background.primary};
  color: ${props => props.theme.colors.text.tertiary};
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${props => props.theme.transitions.default};
  z-index: 10;

  &:hover {
    background: ${props => props.theme.colors.background.hover};
    color: ${props => props.theme.colors.text.primary};
    border-color: ${props => props.theme.colors.border.strong};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.colors.shadow.focus};
  }
`
